import React, { useState } from 'react';
import { Check, Loader2, AlertTriangle, ExternalLink, Star } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { useAnalytics } from '../hooks/useAnalytics';
import ConfirmationModal from './ConfirmationModal';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const { currentUser, userProfile, refreshSubscriptionStatus } = useAuth();
  const { planData, isPro: _isPro } = useUserPlan();
  const { trackSubscriptionModalOpened, trackSubscriptionPlanViewed, trackSubscriptionStarted, trackSubscriptionCheckoutAbandoned } = useAnalytics();

  // Track modal opening
  React.useEffect(() => {
    if (isOpen) {
      trackSubscriptionModalOpened('modal_opened');
    }
  }, [isOpen, trackSubscriptionModalOpened]);

  // Use cached subscription data from AuthContext instead of fetching
  const subscriptionInfo = planData ? {
    plan: planData.plan,
    subscription_status: planData.subscription_status,
    has_active_subscription: planData.has_active_subscription,
    cancel_at_period_end: planData.cancel_at_period_end,
    current_period_end: planData.current_period_end,
    subscription_start_date: userProfile?.createdAt?.toISOString()
  } : null;

  const handleUpgrade = async (planName: string) => {
    if (planName === 'free') {
      onClose();
      return;
    }

    if (!currentUser) {
      toast.error('Please sign in to upgrade your plan');
      return;
    }

    setLoading(planName);
    
    // Track plan selection and upgrade start
    const planPrices: Record<string, number> = {
      'starter_plus': 9.99,
      'creator': 19.99,
      'pro': 39.99,
      'lifetime': 299
    };
    const planPrice = planPrices[planName] || 0;
    trackSubscriptionPlanViewed(planName, planPrice);
    trackSubscriptionStarted(planName, planPrice);
    
    try {
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planName,
          success_url: `${window.location.origin}/dashboard?upgrade=success`,
          cancel_url: `${window.location.origin}/dashboard?upgrade=cancelled`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      trackSubscriptionCheckoutAbandoned(planName, 'checkout_creation_failed');
      toast.error('Failed to start checkout process. Please try again.');
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentUser) {
      toast.error('Please sign in to cancel your subscription');
      return;
    }

    try {
      setCanceling(true);
      
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || 'Failed to cancel subscription';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      toast.success(result.message || 'Subscription canceled successfully');
      
      // Refresh subscription info
      await refreshSubscriptionStatus();
      setShowCancelConfirm(false);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCanceling(false);
    }
  };

  const handleManageBilling = async () => {
    if (!currentUser) {
      toast.error('Please sign in to manage billing');
      return;
    }

    try {
      setOpeningBillingPortal(true);
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/subscription/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle the case where user doesn't have a Stripe customer ID
        if (response.status === 400) {
          if (errorData.detail?.includes('Stripe customer ID')) {
            toast.info('You need an active subscription to access billing management. Please upgrade first.');
            return;
          }
          toast.error(errorData.detail || 'Unable to access billing portal');
          return;
        }
        
        throw new Error(errorData.detail || 'Failed to create portal session');
      }

      const data = await response.json();
      
      // Open portal in new tab
      if (data.portal_url) {
        window.open(data.portal_url, '_blank');
      } else {
        throw new Error('No portal URL received');
      }
      
    } catch (error) {
      console.error('Error opening billing portal:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to open billing portal. Please try again.';
      toast.error(errorMessage);
    } finally {
      setOpeningBillingPortal(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // If user has active subscription, show subscription management
  if (subscriptionInfo?.has_active_subscription) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Subscription Management" size="md">
        <div className="space-y-6">
          {/* Current Subscription Info */}
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-black mb-2">
              {subscriptionInfo.plan === 'pro' ? 'Creator Pro' : 
               subscriptionInfo.plan === 'creator' ? 'Creator' : 
               subscriptionInfo.plan === 'starter_plus' ? 'Starter Plus' :
               subscriptionInfo.plan === 'lifetime' ? 'Lifetime Access' : 'Starter'} Active
            </h2>
            <p className="text-gray-600">
              Thank you for being a valued subscriber!
            </p>
          </div>

          {/* Subscription Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Current Plan:</span>
              <span className="font-medium capitalize">{subscriptionInfo.plan}</span>
            </div>
            
            {subscriptionInfo.subscription_start_date && (
              <div className="flex justify-between">
                <span className="text-gray-600">Member Since:</span>
                <span className="font-medium">{formatDate(subscriptionInfo.subscription_start_date)}</span>
              </div>
            )}

            {subscriptionInfo.current_period_end && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {subscriptionInfo.cancel_at_period_end ? 'Expires:' : 'Next Billing:'}
                </span>
                <span className="font-medium">{formatDate(subscriptionInfo.current_period_end)}</span>
              </div>
            )}
          </div>

          {/* Cancellation Warning */}
          {subscriptionInfo.cancel_at_period_end && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle size={20} className="text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Subscription Canceled</p>
                <p className="text-sm text-red-700 mt-1">
                  Your subscription will end on {subscriptionInfo.current_period_end ? formatDate(subscriptionInfo.current_period_end) : 'the end of your billing period'}. 
                  You'll continue to have access to all pro features until then.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleManageBilling}
              disabled={openingBillingPortal}
              className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {openingBillingPortal ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <ExternalLink size={16} />
              )}
              <span>{openingBillingPortal ? 'Opening Portal...' : 'Manage Billing & Payment'}</span>
            </button>

            {!subscriptionInfo.cancel_at_period_end && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={canceling}
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 text-red-600 border border-red-300 rounded-xl hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {canceling ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <AlertTriangle size={16} />
                )}
                <span>{canceling ? 'Canceling...' : 'Cancel Subscription'}</span>
              </button>
            )}
          </div>

          {/* Billing Portal Info */}
          <div className="text-center text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
            <p>The billing portal allows you to:</p>
            <p>• Update payment methods • Download invoices • View billing history • Change billing address</p>
          </div>

          {/* Support */}
          <div className="text-center space-y-2">
            <div className="text-center text-sm text-gray-500">
              Need help? <a href="https://twitter.com/A5omic" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 hover:underline">Contact Support</a>
            </div>
          </div>
        </div>

        {/* Cancel Subscription Confirmation Modal */}
        <ConfirmationModal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
          title="Cancel Subscription"
          message="Are you sure you want to cancel your subscription? You will keep access until the end of your billing period, but won't be charged again."
          confirmText="Cancel Subscription"
          cancelText="Keep Subscription"
          type="danger"
          icon={<AlertTriangle size={24} />}
          isLoading={canceling}
        />
      </Modal>
    );
  }

  // Default subscription upgrade modal for free users
  const plans = [
    {
      name: 'Free',
      displayName: 'Starter',
      price: '$0',
      period: 'forever',
      description: 'Perfect for testing the waters',
      videoLimit: '3 videos per month',
      storage: '1GB storage',
      features: [
        '3 videos per month',
        'Watermark on exports',
        '720p max quality',
        'Basic AI analysis',
        'Community support'
      ],
      buttonText: 'Get Started Free',
      buttonAction: () => handleUpgrade('free'),
      popular: false
    },
    {
      name: 'Starter_Plus',
      displayName: 'Starter Plus',
      price: '$9.99',
      period: '/month',
      description: 'Great for casual creators',
      videoLimit: '10 videos/month',
      storage: '5GB storage',
      features: [
        '10 videos per month',
        'No watermarks ⭐',
        'All export formats',
        'Clip conversion ⭐',
        'YouTube downloader ⭐',
        'Email support'
      ],
      buttonText: 'Get Started',
      buttonAction: () => handleUpgrade('starter_plus'),
      popular: true
    },
    // {
    //   name: 'Creator',
    //   displayName: 'Creator',
    //   price: '$19.99',
    //   period: '/month',
    //   description: 'Perfect for growing creators',
    //   videoLimit: '20 videos/month',
    //   storage: '15GB storage',
    //   features: [
    //     '20 videos per month',
    //     'No watermarks ⭐',
    //     'Priority processing',
    //     'All export formats',
    //     'Clip conversion ⭐',
    //     'YouTube downloader ⭐',
    //     'Priority email support',
    //     'Advanced AI analysis'
    //   ],
    //   buttonText: 'Start creating',
    //   buttonAction: () => handleUpgrade('creator'),
    //   popular: true
    // },
    {
      name: 'Pro',
      displayName: 'Pro',
      price: '$39.99',
      period: '/month',
      description: 'For serious content creators',
      videoLimit: '50 videos/month',
      storage: '50GB storage',
      features: [
        '50 videos per month',
        'No watermarks ⭐',
        'Priority processing (2x faster)',
        'All export formats',
        'Clip conversion ⭐',
        'YouTube downloader ⭐',
        'Priority email support',
        'Advanced AI analysis',
        'Custom clip lengths',
        'API access (coming soon)'
      ],
      buttonText: 'Go Pro',
      buttonAction: () => handleUpgrade('pro'),
      popular: false
    },
    {
      name: 'Lifetime',
      displayName: 'Lifetime',
      price: '$299',
      period: 'one-time',
      description: 'Pay once, use forever',
      videoLimit: '50 videos/month',
      storage: '50GB storage',
      features: [
        '50 videos per month',
        'No watermarks ⭐',
        'Priority processing (2x faster)',
        'All export formats',
        'Clip conversion ⭐',
        'YouTube downloader ⭐',
        'Priority email support',
        'Advanced AI analysis',
        'Custom clip lengths',
        'API access (coming soon)',
        'Lifetime access 🎉'
      ],
      buttonText: 'Get Lifetime',
      buttonAction: () => handleUpgrade('lifetime'),
      popular: false
    }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose Your Plan" size="xl">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4 text-black">Choose Your Plan</h2>
          <p className="text-lg text-gray-600 mb-6">
            Professional tools for content creators
          </p>
          
          {/* Early Adopter Pricing */}
          <div className="mb-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg p-3">
            <div className="flex items-center justify-center space-x-2">
              <Star className="w-4 h-4 fill-white" />
              <span className="font-bold text-sm">🚀 Early Adopter Pricing</span>
              <Star className="w-4 h-4 fill-white" />
            </div>
            <div className="text-xs text-center text-red-100 mt-1">Lock in your rate before prices increase</div>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="text-center">
              <div className="text-2xl font-bold text-black">⚡</div>
              <div className="text-sm text-gray-600">10x Faster</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-black">🎯</div>
              <div className="text-sm text-gray-600">AI-Powered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-black">🚀</div>
              <div className="text-sm text-gray-600">Indie Made</div>
            </div>
          </div>
        </div>
        
        {/* Plans Grid - Compact 2-row layout */}
        <div className="space-y-6">
          {/* Monthly Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 justify-items-center max-w-4xl mx-auto">
            {plans.filter(p => p.name !== 'Lifetime').map((plan, index) => (
              <div 
                key={index}
                className={`relative flex flex-col rounded-xl border-2 transition-all w-full max-w-xs ${
                  plan.popular 
                    ? 'border-red-500 bg-white shadow-lg ring-2 ring-red-100' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-red-500 text-white px-3 py-0.5 rounded-full text-xs font-bold">
                      POPULAR
                    </span>
                  </div>
                )}
                
                <div className="p-4 flex-1">
                  <div className="text-center mb-4">
                    <h3 className="text-base font-bold mb-1">{plan.displayName}</h3>
                    <div className="mb-1">
                      <span className="text-2xl font-bold">{plan.price}</span>
                      <span className="text-gray-500 text-xs">{plan.period}</span>
                    </div>
                    <p className="text-xs text-gray-600">{plan.description}</p>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="bg-gray-50 rounded p-2 text-center">
                      <div className="font-bold text-sm">{plan.videoLimit}</div>
                      <div className="text-xs text-gray-600">{plan.storage}</div>
                    </div>
                    
                    <ul className="space-y-1.5">
                      {plan.features.slice(0, 4).map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start text-xs">
                          <Check size={14} className="text-red-500 mr-1.5 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                      {plan.features.length > 4 && (
                        <li className="text-xs text-gray-500 pl-5">
                          +{plan.features.length - 4} more
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 pt-0">
                  <button 
                    onClick={plan.buttonAction}
                    disabled={loading === plan.name.toLowerCase()}
                    className={`w-full py-2.5 px-3 rounded-lg font-semibold text-sm transition-all ${
                      plan.popular 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : plan.name === 'Free'
                        ? 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    } ${loading === plan.name.toLowerCase() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading === plan.name.toLowerCase() ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        Processing...
                      </div>
                    ) : (
                      plan.buttonText
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Lifetime Plan - Compact Featured */}
          {plans.filter(p => p.name === 'Lifetime').map((plan, index) => (
            <div key={index} className="relative">
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-red-500 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center shadow-lg">
                  <Star className="w-3 h-3 mr-1 fill-white" />
                  BEST VALUE
                </span>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-red-500 overflow-hidden">
                <div className="p-6">
                  <div className="grid md:grid-cols-3 gap-6 items-center">
                    {/* Plan Info */}
                    <div className="md:col-span-1">
                      <div className="inline-block px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-bold mb-2">
                        ONE-TIME
                      </div>
                      <h3 className="text-2xl font-bold mb-2 text-white">{plan.displayName}</h3>
                      <div className="mb-3">
                        <span className="text-4xl font-bold text-white">{plan.price}</span>
                        <span className="text-gray-400 text-sm ml-1">{plan.period}</span>
                      </div>
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="bg-white/10 rounded p-2 flex-1 text-center">
                          <div className="font-bold text-white text-sm">{plan.videoLimit}</div>
                          <div className="text-xs text-gray-400">{plan.storage}</div>
                        </div>
                        <div className="bg-white/10 rounded p-2 flex-1 text-center">
                          <div className="font-bold text-red-400 text-sm">Forever</div>
                          <div className="text-xs text-gray-400">No fees</div>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="md:col-span-1">
                      <ul className="space-y-2">
                        {plan.features.slice(0, 5).map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start text-sm">
                            <Check size={14} className="text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <div className="md:col-span-1">
                      <div className="text-center mb-3">
                        <div className="text-xl font-bold text-red-400">Save $180/year</div>
                        <div className="text-xs text-gray-400">vs. monthly Pro ($39.99/mo)</div>
                      </div>
                      <button 
                        onClick={plan.buttonAction}
                        disabled={loading === plan.name.toLowerCase()}
                        className="w-full bg-red-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-red-600 transition-all shadow-lg disabled:opacity-50"
                      >
                        {loading === plan.name.toLowerCase() ? (
                          <div className="flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Processing...
                          </div>
                        ) : (
                          plan.buttonText
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Trust signals */}
        <div className="text-center border-t pt-6">
          <div className="flex justify-center items-center space-x-6 text-sm text-gray-600">
            <span>Secure payments</span>
            <span>•</span>
            <span>24/7 support</span>
            <span>•</span>
            <span>Cancel anytime</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Professional quality tools with instant access.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionModal;